/**
 * ImportGuidelineDialog — admin flow to create a new guideline from a PDF.
 *
 * Ported from digital_sizer's ImportPdfDialog, but reworked to fit eastviewos:
 * the source did a PDF → markdown → HTML → Tiptap → markdown round-trip
 * (marked + turndown + Tiptap). Here we keep the markdown the backend extracts
 * and let the user polish it directly in a raw-markdown editor with a live
 * preview (the same GuidelineMarkdown renderer the reader uses), then POST it.
 *
 *   1. Upload a PDF  → POST /guidelines/import-pdf  → extracted markdown
 *   2. Edit slug / title / program + polish markdown (live preview)
 *   3. Save          → POST /guidelines/            → new guideline
 */
import { useCallback, useRef, useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import CloseRounded from "@mui/icons-material/CloseRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { guidelinesApi } from "../../../api/guidelinesApi";
import GuidelineMarkdown from "./GuidelineMarkdown";

const PROGRAMS = ["RTL", "GUC", "DSCR"];

const sanitizeSlug = (s) =>
  String(s)
    .replace(/\.pdf$/i, "")
    .replace(/[^A-Za-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase()
    .slice(0, 60);

const errMessage = (e) =>
  e?.response?.data?.detail ||
  e?.response?.data?.error ||
  e?.message ||
  "Something went wrong";

export default function ImportGuidelineDialog({ open, onClose, onSaved }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(null); // "upload" | "save" | null
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);

  const [markdown, setMarkdown] = useState("");
  const [filename, setFilename] = useState("");
  const [pageCount, setPageCount] = useState(0);
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [program, setProgram] = useState("RTL");

  const reset = useCallback(() => {
    setBusy(null);
    setError(null);
    setDragging(false);
    setMarkdown("");
    setFilename("");
    setPageCount(0);
    setSlug("");
    setTitle("");
    setProgram("RTL");
  }, []);

  const handleClose = useCallback(() => {
    if (busy) return;
    reset();
    onClose();
  }, [busy, reset, onClose]);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (file.type && file.type !== "application/pdf") {
      setError("Please choose a PDF file.");
      return;
    }
    setError(null);
    setBusy("upload");
    setFilename(file.name);
    try {
      const data = await guidelinesApi.importPdf(file);
      setMarkdown(data.markdown || "");
      setPageCount(data.page_count || 0);
      setSlug((prev) => prev || sanitizeSlug(file.name));
      setTitle((prev) => prev || file.name.replace(/\.pdf$/i, ""));
    } catch (e) {
      setError(`Import failed: ${errMessage(e)}`);
      setFilename("");
    } finally {
      setBusy(null);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!slug.trim() || !title.trim()) {
      setError("Slug and title are required.");
      return;
    }
    setError(null);
    setBusy("save");
    try {
      await guidelinesApi.create({
        slug: slug.trim(),
        title: title.trim(),
        program,
        markdown,
        change_summary: `imported from ${filename}`,
      });
      const newSlug = slug.trim();
      reset();
      onSaved(newSlug);
    } catch (e) {
      setError(`Save failed: ${errMessage(e)}`);
      setBusy(null);
    }
  }, [slug, title, program, markdown, filename, reset, onSaved]);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragging(false);
      if (busy) return;
      handleFile(e.dataTransfer.files?.[0]);
    },
    [busy, handleFile],
  );

  const hasContent = Boolean(markdown);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          height: hasContent ? "85vh" : "auto",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Flx
        ac
        jb
        g={1}
        sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #eef2f5", flex: "0 0 auto" }}
      >
        <Txt sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
          Import PDF as new guideline
        </Txt>
        <IconButton size="small" onClick={handleClose} disabled={Boolean(busy)}>
          <CloseRounded sx={{ fontSize: 18 }} />
        </IconButton>
      </Flx>

      {/* Body */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!hasContent ? (
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              if (!busy) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => !busy && fileRef.current?.click()}
            sx={{
              cursor: busy ? "default" : "pointer",
              border: "2px dashed",
              borderColor: dragging ? "#1F5AF2" : "#cbd5e1",
              bgcolor: dragging ? "#f1f6f9" : "#fafbfc",
              borderRadius: 2,
              py: 7,
              px: 3,
              textAlign: "center",
              transition: "border-color 0.15s, background 0.15s",
            }}
          >
            <UploadFileOutlined sx={{ fontSize: 44, color: "#1F5AF2", mb: 1.5 }} />
            <Txt sx={{ fontSize: 15, fontWeight: 600, color: "#0f172a", mb: 0.5 }}>
              {busy === "upload" ? "Extracting text…" : "Drag a PDF here, or click to choose"}
            </Txt>
            <Txt sx={{ fontSize: 12.5, color: "#94a3b8" }}>
              Text is extracted and converted to markdown for you to polish.
            </Txt>
            {busy === "upload" && (
              <LinearProgress sx={{ mt: 3, mx: "auto", maxWidth: 320 }} />
            )}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              hidden
              onChange={(e) => {
                handleFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </Box>
        ) : (
          <>
            <Flx ac g={1} wrap sx={{ mb: 0.75 }}>
              <Txt sx={{ fontSize: 12.5, color: "#64748b" }}>
                Imported <strong>{pageCount}</strong> page{pageCount === 1 ? "" : "s"} from{" "}
                <strong>{filename}</strong>
              </Txt>
              <Button
                size="small"
                startIcon={<RestartAltRounded sx={{ fontSize: 16 }} />}
                onClick={reset}
                disabled={Boolean(busy)}
                sx={{ textTransform: "none", color: "#64748b" }}
              >
                Start over
              </Button>
            </Flx>

            <Flx g={1.5} wrap sx={{ mb: 1.5 }}>
              <TextField
                label="Slug (URL id)"
                size="small"
                value={slug}
                onChange={(e) => setSlug(sanitizeSlug(e.target.value))}
                sx={{ flex: "1 1 200px" }}
                required
              />
              <TextField
                label="Title"
                size="small"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={{ flex: "2 1 260px" }}
                required
              />
              <TextField
                select
                label="Program"
                size="small"
                value={program}
                onChange={(e) => setProgram(e.target.value)}
                sx={{ flex: "0 1 140px", minWidth: 120 }}
              >
                {PROGRAMS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            </Flx>

            {/* Split editor: raw markdown (left) · live preview (right) */}
            <Flx g={1.5} sx={{ flex: 1, minHeight: 0 }}>
              <Flx column sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                <Txt
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "#94a3b8",
                    mb: 0.5,
                  }}
                >
                  Markdown
                </Txt>
                <TextField
                  multiline
                  value={markdown}
                  onChange={(e) => setMarkdown(e.target.value)}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    "& .MuiInputBase-root": {
                      height: "100%",
                      alignItems: "stretch",
                      p: 0,
                      overflow: "hidden",
                    },
                    "& .MuiInputBase-inputMultiline": {
                      height: "100% !important",
                      overflowY: "auto !important",
                      boxSizing: "border-box",
                      px: 1.75,
                      py: 1.5,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: 12.5,
                      lineHeight: 1.6,
                    },
                  }}
                />
              </Flx>
              <Flx column sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                <Txt
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "#94a3b8",
                    mb: 0.5,
                  }}
                >
                  Preview
                </Txt>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    border: "1px solid #e5e7eb",
                    borderRadius: 1.5,
                    px: 2.5,
                    py: 2,
                    bgcolor: "#fff",
                  }}
                >
                  <GuidelineMarkdown markdown={markdown} />
                </Box>
              </Flx>
            </Flx>
          </>
        )}
      </Box>

      {/* Footer */}
      <Flx
        ac
        end
        g={1}
        sx={{ px: 2.5, py: 1.75, borderTop: "1px solid #eef2f5", flex: "0 0 auto" }}
      >
        <Button onClick={handleClose} disabled={Boolean(busy)} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        {hasContent && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={Boolean(busy) || !slug.trim() || !title.trim()}
            sx={{ textTransform: "none", bgcolor: "#1F5AF2", "&:hover": { bgcolor: "#1648CC" } }}
          >
            {busy === "save" ? "Saving…" : "Save guideline"}
          </Button>
        )}
      </Flx>
    </Dialog>
  );
}
